'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { SpaceMemberRoles } from '@shared/all/domains/spaces';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useNavigator from '@shared/utils/hooks/useNavigator';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useSocket from '@shared/utils/hooks/useSocket';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import useOnScreen from '../../hooks/useOnScreen';
import { client } from '../../utils/trpc';
import PostItemActions from './PostItemActions';
import PostItemBody from './PostItemBody';
import PostItemHead from './PostItemHead';
import PostViewTracker from './PostViewTracker';
import TopAnswerPreview from './TopAnswerPreview';
type Props = {
  spaceSlug?: string;
  post: Post;
  isFullLength: boolean;
  disableLink: boolean;
  isInView: boolean;
  isHeadless?: boolean;
  isThread?: boolean;
  disableTracking?: boolean;
  uniqueId?: string;
  minified?: boolean;
  disableReply?: boolean;
  onCardClick?: () => void;
  // Open the editor as soon as the post renders, for viewers allowed to edit
  // it. The board card detail modal sets this so opening a card you can edit
  // lands in the editor instead of asking for a second click on the body.
  // Board cards pass the list's factiiiId so the card hides that factiii from
  // its breakdown (redundant with the list it sits in).
  ignoreFactiiiId?: number;
};
const PostItem = props => {
  const $ = _c(87);
  const postRef = useRef(null);
  const {
    isVisible: postOnScreen
  } = useOnScreen(postRef);
  const router = useRouter();
  const pathname = usePathname();
  const {
    setBackNavigation
  } = useNavigator();
  const {
    currentUser
  } = useUser();
  const {
    isNsfw,
    isPolitics,
    isTroll
  } = usePostsShared(client, props.uniqueId);
  const {
    joinPostStats,
    leavePostStats
  } = useSocket();
  let t0;
  if ($[0] !== isNsfw || $[1] !== props.post.postFactiiisCount) {
    t0 = isNsfw(props.post.postFactiiisCount);
    $[0] = isNsfw;
    $[1] = props.post.postFactiiisCount;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [nsfw, setNsfw] = useState(t0);
  let t1;
  if ($[3] !== isPolitics || $[4] !== props.post.postFactiiisCount || $[5] !== props.post.space) {
    t1 = isPolitics(props.post.postFactiiisCount, props.post.space);
    $[3] = isPolitics;
    $[4] = props.post.postFactiiisCount;
    $[5] = props.post.space;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const [politics, setPolitics] = useState(t1);
  let t2;
  if ($[7] !== isTroll || $[8] !== props.post.postFactiiisCount) {
    t2 = isTroll(props.post.postFactiiisCount);
    $[7] = isTroll;
    $[8] = props.post.postFactiiisCount;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const [troll, setTroll] = useState(t2);
  const [editOpen, setEditOpen] = useState(false);
  const isBoardLayoutSpace = props.post.pos != null;
  const isAuthor = props.post.user?.username === currentUser.username;
  let t3;
  if ($[10] !== currentUser.username || $[11] !== props.post.space) {
    t3 = ["space-membership", props.post.space, currentUser.username, "moderator-check"];
    $[10] = currentUser.username;
    $[11] = props.post.space;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== props.post.space) {
    t4 = () => unwrapFromResult(client.spaces.getMembership.query({
      slug: props.post.space || ""
    })).then(_temp);
    $[13] = props.post.space;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = isBoardLayoutSpace && !isAuthor && Boolean(props.post.space) && Boolean(currentUser.isAuthenticated);
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      staleTime: 300000,
      enabled: t5
    };
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const {
    data: isBoardModerator
  } = useQuery(t6);
  const canEditCard = isBoardLayoutSpace && (isAuthor || Boolean(isBoardModerator));
  let t7;
  if ($[19] !== joinPostStats || $[20] !== leavePostStats || $[21] !== postOnScreen || $[22] !== props.post.id) {
    t7 = () => {
      if (postOnScreen) {
        joinPostStats(props.post.id);
      } else {
        leavePostStats(props.post.id);
      }
      return () => {
        leavePostStats(props.post.id);
      };
    };
    $[19] = joinPostStats;
    $[20] = leavePostStats;
    $[21] = postOnScreen;
    $[22] = props.post.id;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== postOnScreen || $[25] !== props.post.id) {
    t8 = [postOnScreen, props.post.id];
    $[24] = postOnScreen;
    $[25] = props.post.id;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  useEffect(t7, t8);
  const isHeadlessPost = props.isHeadless ?? false;
  const postPath = props.post.slug ? `/post/${props.post.slug}` : `/post/${props.post.id}`;
  const postHref = !props.post.isServerPrivate && props.post.isPrivate ? `/private-post/${props.post.id}` : postPath;
  let t9;
  if ($[27] !== pathname || $[28] !== postHref || $[29] !== props || $[30] !== router || $[31] !== setBackNavigation) {
    t9 = e => {
      if (props.disableLink) {
        props.onCardClick?.();
        return;
      }
      const target = e.target;
      if (target instanceof HTMLElement && !target.closest("#inputId")) {
        const factiiiPageMatch = pathname?.match(/^\/fids\/(\d+)/);
        let context;
        if (factiiiPageMatch) {
          const factiiiId = parseInt(factiiiPageMatch[1]);
          const matchingFactiii = props.post.postFactiiisCount.find(pfc => pfc.factiiiId === factiiiId);
          if (matchingFactiii) {
            context = matchingFactiii.name;
          }
        }
        setBackNavigation({
          path: pathname || "/",
          context
        });
        router.push(postHref);
      }
    };
    $[27] = pathname;
    $[28] = postHref;
    $[29] = props;
    $[30] = router;
    $[31] = setBackNavigation;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const handlePostClick = t9;
  const t10 = `w-full grow rounded-lg border border-ds-border-subtle bg-background-light transition-all duration-300 ease-in-out md:mx-0 ${props.minified ? "p-3 hover:border-ds-border-hover hover:bg-background-light" : "p-4 hover:border-ds-border-hover hover:bg-background-light"} ${props.disableLink ? "" : "cursor-pointer"}`;
  const t11 = props.disableLink ? undefined : postHref;
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setEditOpen(false);
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== editOpen || $[35] !== isHeadlessPost || $[36] !== nsfw || $[37] !== politics || $[38] !== props.ignoreFactiiiId || $[39] !== props.minified || $[40] !== props.post || $[41] !== props.spaceSlug || $[42] !== props.uniqueId || $[43] !== t11 || $[44] !== troll) {
    t13 = <PostItemHead spaceSlug={props.spaceSlug} post={props.post} politics={politics} nsfw={nsfw} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} ignoreFactiiiId={props.ignoreFactiiiId} uniqueId={props.uniqueId} isHeadless={isHeadlessPost} minified={props.minified} postHref={t11} externalEditOpen={editOpen} onExternalEditClose={t12} />;
    $[34] = editOpen;
    $[35] = isHeadlessPost;
    $[36] = nsfw;
    $[37] = politics;
    $[38] = props.ignoreFactiiiId;
    $[39] = props.minified;
    $[40] = props.post;
    $[41] = props.spaceSlug;
    $[42] = props.uniqueId;
    $[43] = t11;
    $[44] = troll;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const t15 = !props.minified;
  let t16;
  if ($[47] !== canEditCard || $[48] !== props.minified) {
    t16 = canEditCard && !props.minified ? () => setEditOpen(true) : undefined;
    $[47] = canEditCard;
    $[48] = props.minified;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== nsfw || $[51] !== politics || $[52] !== props.disableLink || $[53] !== props.isFullLength || $[54] !== props.isInView || $[55] !== props.minified || $[56] !== props.post || $[57] !== t15 || $[58] !== t16 || $[59] !== troll) {
    t17 = <PostItemBody isFullLength={props.isFullLength} post={props.post} politics={politics} nsfw={nsfw} disableLink={props.disableLink} isInView={props.isInView} troll={troll} ignorePostFactiiiIds={t14} minified={props.minified} fullBleedMedia={t15} onContentEditClick={t16} />;
    $[50] = nsfw;
    $[51] = politics;
    $[52] = props.disableLink;
    $[53] = props.isFullLength;
    $[54] = props.isInView;
    $[55] = props.minified;
    $[56] = props.post;
    $[57] = t15;
    $[58] = t16;
    $[59] = troll;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== props.isFullLength || $[62] !== props.post.topAnswer) {
    t18 = props.post.topAnswer && !props.isFullLength && <TopAnswerPreview answer={props.post.topAnswer} />;
    $[61] = props.isFullLength;
    $[62] = props.post.topAnswer;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  const t19 = props.minified ? "mt-2 pt-2" : "mt-2 pt-3";
  let t20;
  if ($[64] !== nsfw || $[65] !== politics || $[66] !== props.disableReply || $[67] !== props.isThread || $[68] !== props.minified || $[69] !== props.post || $[70] !== props.uniqueId || $[71] !== troll) {
    t20 = <PostItemActions post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} redirectOnReply={props.isThread} uniqueId={props.uniqueId} minified={props.minified} disableReply={props.disableReply} />;
    $[64] = nsfw;
    $[65] = politics;
    $[66] = props.disableReply;
    $[67] = props.isThread;
    $[68] = props.minified;
    $[69] = props.post;
    $[70] = props.uniqueId;
    $[71] = troll;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== t19 || $[74] !== t20) {
    t21 = <div className={t19}>{t20}</div>;
    $[73] = t19;
    $[74] = t20;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== handlePostClick || $[77] !== t10 || $[78] !== t13 || $[79] !== t17 || $[80] !== t18 || $[81] !== t21) {
    t22 = <div ref={postRef} data-post-card="true" className={t10} onClick={handlePostClick}>{t13}{t17}{t18}{t21}</div>;
    $[76] = handlePostClick;
    $[77] = t10;
    $[78] = t13;
    $[79] = t17;
    $[80] = t18;
    $[81] = t21;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  const postContent = t22;
  let t23;
  if ($[83] !== postContent || $[84] !== props.disableTracking || $[85] !== props.post) {
    t23 = <div>{!props.disableTracking || props.post.isPrivate ? <PostViewTracker post={props.post}>{postContent}</PostViewTracker> : postContent}</div>;
    $[83] = postContent;
    $[84] = props.disableTracking;
    $[85] = props.post;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  return t23;
};
export default PostItem;
function _temp(res) {
  return Boolean(res?.roles.includes(SpaceMemberRoles.MODERATOR)) || Boolean(res?.roles.includes(SpaceMemberRoles.OWNER));
}